/**
 * Validation over a scanned identity. Pure: it reports issues, it never throws
 * and never rejects a document. Everything it finds is a flag for the operator,
 * who confirms every field anyway; nothing here blocks on its own. Codes are
 * returned (not messages), so the UI renders them through i18n.
 */

import type { FieldSource, ScannedIdentity } from './ocr-interface'
import type { MrzResult } from './parsers/mrz'

export type IssueCode =
  | 'licence-expired' // 4b is before the rental return date
  | 'licence-dates-order' // 4b is not after 4a
  | 'mrz-check-failed' // a machine-readable check digit did not match
  | 'licence-number-format' // the number does not fit the country's known shape
  | 'name-mismatch' // two sources disagree on the name

export interface ValidationIssue {
  readonly code: IssueCode
  readonly severity: 'warn' | 'error'
  /** The scanned field this is about, when it is about one. */
  readonly field?: keyof ScannedIdentity
}

export interface NameReading {
  readonly surname?: string
  readonly givenNames?: string
  readonly source: FieldSource
}

export interface ValidateContext {
  /** ISO date the vehicle comes back; the licence must still be valid then. */
  readonly returnDate?: string
  /** The machine-readable read, to flag a failed check digit. */
  readonly mrz?: MrzResult | null
  /** ISO 3166-1 alpha-2 of the licence's country, as its own title named it, for the format check. */
  readonly licenceCountry?: string
  /** Every source's name, so two that disagree can be flagged. */
  readonly names?: readonly NameReading[]
}

/** Known licence-number shapes, by ISO 3166-1 alpha-2 (the country the card's
 *  own title names, parsers/eu-licence). Absent ⇒ no format opinion (not flagged). */
const LICENCE_FORMATS: Record<string, RegExp> = {
  IT: /^[A-Z]{2}\d{7}[A-Z]$/, // e.g. RM1234567A
  FR: /^\d{2}[A-Z]{2}\d{5}$/, // e.g. 13AA00002
  ES: /^\d{8}[A-Z]$/, // e.g. 12345678Z
  GB: /^[A-Z9]{5}\d{6}[A-Z9]{2}\d{1}[A-Z]{2}$/,
}

/** A name reduced to letters only, upper-case, for a tolerant comparison. */
function normalizeName(s: string | undefined): string {
  return (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^\p{L}]/gu, '')
    .toUpperCase()
}

export function validate(identity: ScannedIdentity, ctx: ValidateContext = {}): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // The licence must still be valid when the vehicle is returned.
  const expiry = identity.licenceExpiry?.value
  if (expiry && ctx.returnDate && expiry < ctx.returnDate) {
    issues.push({ code: 'licence-expired', severity: 'error', field: 'licenceExpiry' })
  }

  // 4b must come after 4a.
  const issued = identity.licenceIssued?.value
  if (expiry && issued && expiry <= issued) {
    issues.push({ code: 'licence-dates-order', severity: 'warn', field: 'licenceExpiry' })
  }

  // A machine-readable zone whose check digits did not all pass.
  if (ctx.mrz && !ctx.mrz.valid) {
    issues.push({ code: 'mrz-check-failed', severity: 'warn' })
  }

  // Licence number against the country's known shape — flagged, never rejected.
  const number = identity.licenceNumber?.value
  const format = ctx.licenceCountry ? LICENCE_FORMATS[ctx.licenceCountry.toUpperCase()] : undefined
  if (number && format && !format.test(number)) {
    issues.push({ code: 'licence-number-format', severity: 'warn', field: 'licenceNumber' })
  }

  // Two sources that disagree on the name (licence vs ID).
  const names = ctx.names ?? []
  const surnames = new Set(names.map((n) => normalizeName(n.surname)).filter(Boolean))
  const givens = new Set(names.map((n) => normalizeName(n.givenNames)).filter(Boolean))
  if (surnames.size > 1 || givens.size > 1) {
    issues.push({ code: 'name-mismatch', severity: 'warn', field: 'surname' })
  }

  return issues
}
